import React from 'react';
import { SOCIAL_PROFILE_URLS } from '../config/social';

/**
 * Schema.org JSON-LD structured data components for SEO rich snippets
 */

// ============== TYPES ==============

export interface ProfessionalServiceSchema {
    name: string;
    description?: string;
    image?: string;
    telephone?: string;
    address?: {
        locality: string; // City
        region?: string;  // Region/State
        country: string;  // Country code (MA)
    };
    geo?: {
        latitude: number;
        longitude: number;
    };
    aggregateRating?: {
        ratingValue: number;
        reviewCount: number;
    };
    priceRange?: string;
    url?: string;
}

export interface BreadcrumbItem {
    name: string;
    url: string;
}

export interface ProviderTradeFaq {
    q: string;
    a: string;
}

export interface LocalBusinessSchema extends ProfessionalServiceSchema {
    openingHours?: string; // e.g. "Mo-Fr 08:00-18:00"
    paymentAccepted?: string[];
    currenciesAccepted?: string;
}

// ============== HELPERS ==============

const SITE_URL = 'https://www.bricolat.co.ma';
const PUBLIC_PHONE = import.meta.env.VITE_PUBLIC_PHONE?.trim() || '';
const PUBLIC_EMAIL = 'support@bricolat.co.ma';
const ENV_SOCIAL_PROFILES = (import.meta.env.VITE_SOCIAL_PROFILES || '')
    .split(',')
    .map((url: string) => url.trim())
    .filter(Boolean);
// Fall back to the canonical Instagram/Facebook URLs when no env override is set.
const SOCIAL_PROFILES = ENV_SOCIAL_PROFILES.length > 0 ? ENV_SOCIAL_PROFILES : SOCIAL_PROFILE_URLS;

function withOptionalContactAndProfiles(schema: Record<string, unknown>): Record<string, unknown> {
    if (SOCIAL_PROFILES.length > 0) {
        schema.sameAs = SOCIAL_PROFILES;
    }

    if (PUBLIC_PHONE) {
        schema.telephone = PUBLIC_PHONE;
    }

    return schema;
}

function stringifyJsonLd(schema: object): string {
    return JSON.stringify(schema)
        .replace(/</g, '\\u003c')
        .replace(/>/g, '\\u003e')
        .replace(/&/g, '\\u0026');
}

/**
 * Generate ProfessionalService schema for provider profiles
 */
export function generateProfessionalServiceSchema(data: ProfessionalServiceSchema): object {
    const schema: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        name: data.name,
        url: data.url || SITE_URL,
    };

    if (data.description) {
        schema.description = data.description;
    }

    if (data.image) {
        schema.image = data.image.startsWith('http') ? data.image : `${SITE_URL}${data.image}`;
    }

    if (data.telephone) {
        schema.telephone = data.telephone;
    }

    if (data.address) {
        schema.address = {
            '@type': 'PostalAddress',
            addressLocality: data.address.locality,
            addressRegion: data.address.region,
            addressCountry: data.address.country,
        };
    }

    if (data.geo) {
        schema.geo = {
            '@type': 'GeoCoordinates',
            latitude: data.geo.latitude,
            longitude: data.geo.longitude,
        };
    }

    if (data.aggregateRating && data.aggregateRating.reviewCount > 0) {
        schema.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: data.aggregateRating.ratingValue.toFixed(1),
            bestRating: 5,
            worstRating: 1,
            reviewCount: data.aggregateRating.reviewCount,
        };
    }

    if (data.priceRange) {
        schema.priceRange = data.priceRange;
    }

    return schema;
}

/**
 * Generate BreadcrumbList schema for navigation
 */
export function generateBreadcrumbSchema(items: BreadcrumbItem[]): object {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`,
        })),
    };
}

/**
 * Generate WebSite schema for homepage
 */
export function generateWebSiteSchema(): object {
    // SEO-R2: avoid advertising SearchAction for /search because robots.txt disallows that route.
    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Bricolat',
        url: SITE_URL,
        description: 'Trouvez des artisans de confiance au Maroc',
    };
}

/**
 * Generate Organization schema for brand
 */
export function generateOrganizationSchema(): object {
    const schema: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Bricolat',
        url: SITE_URL,
        logo: `${SITE_URL}/logo.png`,
        email: PUBLIC_EMAIL,
        contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            email: PUBLIC_EMAIL,
            availableLanguage: ['French', 'Arabic'],
            ...(PUBLIC_PHONE ? { telephone: PUBLIC_PHONE } : {}),
        },
    };

    return withOptionalContactAndProfiles(schema);
}

export function generateContactPageSchema(): object {
    return withOptionalContactAndProfiles({
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'Contact Bricolat',
        url: `${SITE_URL}/contact`,
        description: 'Contactez Bricolat pour une question client, prestataire, partenariat ou support au Maroc.',
        mainEntity: {
            '@type': 'Organization',
            name: 'Bricolat',
            url: SITE_URL,
            logo: `${SITE_URL}/logo.png`,
            email: PUBLIC_EMAIL,
            contactPoint: {
                '@type': 'ContactPoint',
                contactType: 'customer service',
                email: PUBLIC_EMAIL,
                availableLanguage: ['French', 'Arabic', 'English'],
                ...(PUBLIC_PHONE ? { telephone: PUBLIC_PHONE } : {}),
            },
        },
    });
}

/**
 * Generate Service schema for category/service landing pages
 */
export function generateServiceSchema(data: {
    serviceName: string;
    description?: string;
    url?: string;
    providerName?: string;
    areaServed?: string[];
    aggregateRating?: { ratingValue: number; reviewCount: number };
}): object {
    const schema: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: data.serviceName,
        url: data.url || SITE_URL,
        provider: {
            '@type': 'Organization',
            name: data.providerName || 'Bricolat',
            url: SITE_URL,
        },
    };

    if (data.description) {
        schema.description = data.description;
    }

    if (data.areaServed && data.areaServed.length > 0) {
        schema.areaServed = data.areaServed.map(city => ({
            '@type': 'City',
            name: city,
        }));
    } else {
        schema.areaServed = {
            '@type': 'Country',
            name: 'Morocco',
        };
    }

    if (data.aggregateRating && data.aggregateRating.reviewCount > 0) {
        schema.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: data.aggregateRating.ratingValue.toFixed(1),
            bestRating: 5,
            worstRating: 1,
            reviewCount: data.aggregateRating.reviewCount,
        };
    }

    return schema;
}

/**
 * Generate LocalBusiness schema for the homepage / site-wide
 */
export function generateLocalBusinessSchema(): object {
    const schema: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'HomeAndConstructionBusiness',
        name: 'Bricolat',
        url: SITE_URL,
        logo: `${SITE_URL}/logo.png`,
        description: 'Plateforme marocaine de mise en relation avec des artisans de confiance pour les services à domicile.',
        address: {
            '@type': 'PostalAddress',
            addressLocality: 'Casablanca',
            addressCountry: 'MA',
        },
        areaServed: [
            { '@type': 'City', name: 'Casablanca' },
            { '@type': 'City', name: 'Rabat' },
            { '@type': 'City', name: 'Marrakech' },
            { '@type': 'City', name: 'Tanger' },
            { '@type': 'City', name: 'Fès' },
            { '@type': 'City', name: 'Agadir' },
        ],
        openingHours: 'Mo-Fr 08:00-18:00',
    };

    return withOptionalContactAndProfiles(schema);
}

const PROVIDER_HOWTO_TEXT: Record<string, { name: string; description: string; steps: [string, string][] }> = {
    fr: {
        name: 'Comment rejoindre Bricolat en tant que prestataire',
        description: "Inscrivez-vous sur Bricolat et commencez à recevoir des demandes de clients qualifiés.",
        steps: [
            ['Créez votre profil en 5 minutes', "Ajoutez vos spécialités, votre zone d'intervention, vos tarifs. Simple."],
            ['Recevez des demandes qualifiées', 'Clients réels, projets concrets, filtrés par votre métier et votre ville.'],
            ['Envoyez votre devis, décrochez le job', 'Répondez aux demandes, échangez avec le client, signez et travaillez.'],
        ],
    },
    en: {
        name: 'How to join Bricolat as a service provider',
        description: 'Sign up on Bricolat and start receiving requests from qualified clients.',
        steps: [
            ['Create your profile in 5 minutes', 'Add your specialties, service area and rates. Simple.'],
            ['Receive qualified requests', 'Real clients and concrete projects, filtered by your trade and city.'],
            ['Send your quote and win the job', 'Answer requests, talk with the client, agree and get to work.'],
        ],
    },
    ar: {
        name: 'كيف تنضم إلى Bricolat كمقدم خدمة',
        description: 'سجل في Bricolat وابدأ في استقبال طلبات من زبناء مؤهلين.',
        steps: [
            ['أنشئ ملفك في 5 دقائق', 'أضف تخصصاتك ومنطقة عملك وأسعارك. بكل بساطة.'],
            ['استقبل طلبات مؤهلة', 'زبناء حقيقيون ومشاريع ملموسة، مصنفة حسب حرفتك ومدينتك.'],
            ['أرسل عرض السعر واحصل على العمل', 'أجب عن الطلبات، تواصل مع الزبون، اتفق وابدأ العمل.'],
        ],
    },
    darija: {
        name: 'Kifach tdkhol l Bricolat b7al prestataire',
        description: 'Tsejjel f Bricolat w bda twessel b talabat dyal lkliyan.',
        steps: [
            ['Dir lprofil dyalek f 5 d9aye9', 'Zid l7irfat dyalek, lmantiqa fin khddam w l2atmina. Sahla.'],
            ['Twessel b talabat mzyanin', 'Kliyan b s7i7 w mchari3 wad7in, 3la 7sab l7irfa w lmdina dyalek.'],
            ['Sift devis dyalek w khod lkhdma', 'Jaweb 3la talabat, hder m3a lkliyan, tfa9 w bda lkhdma.'],
        ],
    },
    darija_ar: {
        name: 'كيفاش تدخل لبريكولا بحال بريستاتير',
        description: 'تسجل فبريكولا وبدا توصل بطلبات الكليان.',
        steps: [
            ['دير البروفيل ديالك ف5 دقايق', 'زيد الحرف ديالك، المنطقة فين خدام والأثمنة. ساهلة.'],
            ['توصل بطلبات مزيانين', 'كليان بصح ومشاريع واضحين، على حساب الحرفة والمدينة ديالك.'],
            ['صيفط الدوفي ديالك وخود الخدمة', 'جاوب على الطلبات، هضر مع الكليان، تفق وبدا الخدمة.'],
        ],
    },
};

export function generateProviderHowToSchema(language = 'fr'): object {
    const text = PROVIDER_HOWTO_TEXT[language] ?? PROVIDER_HOWTO_TEXT.fr;
    return {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: text.name,
        description: text.description,
        step: text.steps.map(([name, stepText], index) => ({
            '@type': 'HowToStep',
            position: index + 1,
            name,
            text: stepText,
        })),
    };
}

export function generateFaqSchema(faq: ProviderTradeFaq[]): object {
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: {
                '@type': 'Answer',
                text: item.a,
            },
        })),
    };
}

export function generateCityLocalBusinessSchema(city: string): object {
    return {
        '@context': 'https://schema.org',
        '@type': 'HomeAndConstructionBusiness',
        name: `Bricolat ${city}`,
        url: `${SITE_URL}/devenir-prestataire`,
        description: `Plateforme Bricolat pour aider les artisans à trouver des clients à ${city}.`,
        address: {
            '@type': 'PostalAddress',
            addressLocality: city,
            addressCountry: 'MA',
        },
        areaServed: {
            '@type': 'City',
            name: city,
        },
        provider: {
            '@type': 'Organization',
            name: 'Bricolat',
            url: SITE_URL,
        },
    };
}

// ============== COMPONENT ==============

interface SchemaOrgProps {
    schema: object | object[];
}

/**
 * Inject JSON-LD structured data into page head
 * Supports single schema or array of schemas
 */
export const SchemaOrg: React.FC<SchemaOrgProps> = ({ schema }) => {
    const schemas = Array.isArray(schema) ? schema : [schema];

    return (
        <>
            {schemas.map((s, index) => (
                <script key={index} type="application/ld+json" dangerouslySetInnerHTML={{ __html: stringifyJsonLd(s) }} />
            ))}
        </>
    );
};

export default SchemaOrg;
